/**
 * hooks/useWebSocket.ts
 * ─────────────────────────────────────────────────────────────
 * Manages the WebSocket connection to the backend.
 * Handles reconnection, message routing, and subscription management.
 * ─────────────────────────────────────────────────────────────
 */

import { useEffect, useRef, useCallback } from 'react';
import { useWs, useStore }                from '../store';
import type { Gapper }                    from '../types';

const WS_URL           = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`;
const RECONNECT_DELAY  = 3000;
const MAX_RECONNECTS   = 10;

export function useWebSocket() {
  const ws              = useRef<WebSocket | null>(null);
  const reconnectCount  = useRef(0);
  const reconnectTimer  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const subscriptions   = useRef<Set<string>>(new Set());

  const { setStatus, setPing } = useWs();
  const store = useStore;

  const connect = useCallback(() => {
    if (ws.current?.readyState === WebSocket.OPEN) return;

    setStatus('connecting');

    try {
      ws.current = new WebSocket(WS_URL);

      ws.current.onopen = () => {
        setStatus('connected');
        reconnectCount.current = 0;

        // Re-subscribe to all previously subscribed symbols
        if (subscriptions.current.size > 0) {
          ws.current?.send(JSON.stringify({
            action: 'subscribe',
            symbols: [...subscriptions.current],
          }));
        }
      };

      ws.current.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data as string) as {
            type: string;
            payload: unknown;
            timestamp: string;
          };

          handleMessage(msg.type, msg.payload);
        } catch {
          console.warn('[WS] Failed to parse message');
        }
      };

      ws.current.onclose = () => {
        setStatus('disconnected');
        scheduleReconnect();
      };

      ws.current.onerror = () => {
        setStatus('error');
        ws.current?.close();
      };
    } catch {
      setStatus('error');
      scheduleReconnect();
    }
  }, [setStatus]);

  const scheduleReconnect = useCallback(() => {
    if (reconnectCount.current >= MAX_RECONNECTS) return;
    reconnectCount.current++;

    reconnectTimer.current = setTimeout(() => {
      connect();
    }, RECONNECT_DELAY * Math.min(reconnectCount.current, 4));
  }, [connect]);

  /** Route incoming WebSocket messages to the appropriate store actions */
  function handleMessage(type: string, payload: unknown) {
    switch (type) {
      case 'bar':
        // TODO: Update live price for currently selected ticker
        // store.getState().updateLivePrice(payload)
        break;

      case 'quote':
        // TODO: Update bid/ask in options chain
        break;

      case 'scanner-update':
        store.getState().upsertGapper(payload as Gapper);
        break;

      case 'scan-complete':
        store.getState().setScanning(false);
        store.getState().setLastScan(Date.now());
        break;

      case 'scan-error': {
        const message = (payload as { message?: string })?.message ?? 'Scan failed';
        store.getState().setScanning(false);
        store.getState().setScanError(message);
        break;
      }

      case 'pong':
        setPing(Date.now());
        break;

      default:
        // Unknown message type — ignore
        break;
    }
  }

  /** Subscribe to real-time updates for a list of symbols */
  const subscribe = useCallback((symbols: string[]) => {
    symbols.forEach(s => subscriptions.current.add(s.toUpperCase()));

    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({ action: 'subscribe', symbols }));
    }
  }, []);

  /** Unsubscribe from real-time updates */
  const unsubscribe = useCallback((symbols: string[]) => {
    symbols.forEach(s => subscriptions.current.delete(s.toUpperCase()));

    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({ action: 'unsubscribe', symbols }));
    }
  }, []);

  /** Send a ping to check connection health */
  const ping = useCallback(() => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({ action: 'ping' }));
    }
  }, []);

  useEffect(() => {
    connect();

    // Ping every 30 seconds to keep connection alive
    const pingInterval = setInterval(ping, 30_000);

    return () => {
      clearInterval(pingInterval);
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (ws.current) ws.current.onclose = null;
      ws.current?.close();
    };
  }, [connect, ping]);

  return { subscribe, unsubscribe, ping };
}
